/**
 * "Delete this skill?", shared by the list's row menu and the skill page.
 * Delete is the danger action; a skill that isn't archived can be archived
 * instead (it keeps its history and stops running). After a delete, a toast
 * offers Undo, which restores the skill and opens it again.
 */
import { useState, type FC } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";

import { invalidateActiveFirmSkills } from "@/features/firmKnowledge/hooks/useActiveFirmSkills";
import type { FirmSkillsState } from "@/features/firmKnowledge/hooks/useFirmSkills";
import { automationsPath } from "@/features/automations/routes";
import type { FirmSkill } from "@/features/firmKnowledge/types";

type Props = {
  skill: Pick<FirmSkill, "id" | "title" | "slug" | "status"> | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  skills: Pick<FirmSkillsState, "remove" | "restore" | "setStatus">;
  /** After the delete (the page leaves for the list). */
  onDeleted?: () => void;
};

const UNDO_MS = 10_000;

export const DeleteSkillDialog: FC<Props> = ({
  skill,
  open,
  onOpenChange,
  skills,
  onDeleted
}) => {
  const navigate = useNavigate();
  const [busy, setBusy] = useState<"delete" | "archive" | null>(null);
  if (!skill) return null;
  const name = skill.title || skill.slug;

  const undo = async (skillId: string) => {
    try {
      await skills.restore(skillId);
      invalidateActiveFirmSkills();
      navigate(automationsPath({ skillId }));
    } catch (e) {
      toast.error(
        e instanceof Error ? e.message : "Could not bring the automation back."
      );
    }
  };

  const remove = async () => {
    setBusy("delete");
    try {
      await skills.remove(skill.id);
      invalidateActiveFirmSkills();
      onOpenChange(false);
      onDeleted?.();
      toast(`Deleted “${name}”`, {
        duration: UNDO_MS,
        action: { label: "Undo", onClick: () => void undo(skill.id) }
      });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not delete it.");
    } finally {
      setBusy(null);
    }
  };

  const archive = async () => {
    setBusy("archive");
    try {
      await skills.setStatus(skill.id, "archived");
      invalidateActiveFirmSkills();
      onOpenChange(false);
      toast.success(`Archived “${name}”`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not archive it.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid="skill-delete-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Delete “{name}”?</AlertDialogTitle>
          <AlertDialogDescription>
            {skill.status === "archived"
              ? "Its runs stay in their case threads."
              : "Its runs stay in their case threads. Archive it instead to stop it running and keep it in the list."}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy !== null}>Keep</AlertDialogCancel>
          {skill.status !== "archived" ? (
            <Button
              variant="secondary"
              disabled={busy !== null}
              onClick={() => void archive()}
              data-testid="skill-delete-archive"
            >
              {busy === "archive" ? <Spinner className="size-3.5" /> : null}
              Archive Instead
            </Button>
          ) : null}
          <Button
            variant="danger"
            disabled={busy !== null}
            onClick={() => void remove()}
            data-testid="skill-delete-confirm"
          >
            {busy === "delete" ? <Spinner className="size-3.5" /> : null}
            Delete
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
